/**
 * What a quarantined write *was*, in the terms of the thing itself.
 *
 * **Keyed on the write's scope, never on its error code.** A refusal says why
 * the server declined; it says nothing about what somebody is standing there
 * holding. Those are different questions and this screen is the one place both
 * get asked at once — the row already carries the refusal, worded once for the
 * whole app in `error-messages.ts`, and what it was missing is the other half.
 *
 * **Why not copy for the code instead.** The refusal that strands a whole shape
 * of queued write — a required field added to a terminal route, described in
 * blossom-rest-api#301 — arrives as `VALIDATION_FAILED`, and that code is raised
 * app-wide for genuine software faults where "report it" is the honest answer.
 * FE041 left it on the generic fallback for that reason and the reasoning holds:
 * one sentence keyed on the code would have to serve both a form that disagrees
 * with the server about a rule and a day's takings that cannot be sent. Keying
 * on the scope splits them without weakening either.
 *
 * **Scope is the right key and is already here.** It is the same string the API
 * handler declares on its own `@Idempotent`, it is stored per entry, and it
 * crosses the bridge on `SyncBlockedEntry` — so nothing new is carried to make
 * this work. It also survives the day #301 lands: when a route exists to record
 * one of these, what changes is the instruction, not what the write was.
 */

export interface QueuedWriteDescription {
  /**
   * The write as a person would name it. A noun phrase, because it opens the
   * row and is also spoken as part of the row's control ("Send again — a sale
   * from …").
   */
  readonly what: string;
  /**
   * What is already true in the park because this happened at the till, and is
   * not true on the server.
   *
   * This is the sentence the ticket exists for. Somebody reading the row is
   * working out what they are missing, and every phrasing in terms of the
   * request — refused, rejected, failed to send — answers a different question.
   * Money and paper, not HTTP.
   *
   * **No amounts, and none are available to add.** The bridge carries the scope
   * and not the payload, which is the right way round: a figure on this screen
   * would be the one number a manager reconciles against, taken from a copy of
   * a write the server has never confirmed.
   */
  readonly stake: string;
  /**
   * Whether the server must already hold the record this acts on.
   *
   * True for a void and a refund, false for the ticket and the sale that create
   * the record. It is the difference between a write that can land on its own
   * and one that cannot land before something else does.
   *
   * **This is as far as the honest answer goes, and it is a deliberate stop.**
   * A till can hold a queued ticket and a queued void of that same ticket; the
   * drain steps over a quarantined entry rather than stopping, so both reach
   * this list and the second is a consequence of the first. Saying *which*
   * ticket would need the parent id, and `SyncBlockedEntry` exposes the scope
   * and not the payload. Rather than widen the bridge on the way past, the
   * screen says the conditional thing that is true of every such write — it
   * needs its record to be there — and leaves the reader to match the pair up
   * from a list that is already in the order the till made them.
   */
  readonly actsOnAnExistingRecord: boolean;
}

/**
 * The four writes a terminal queues, and nothing else.
 *
 * Each key is a scope taken from the desktop call site that enqueues it, which
 * in turn matches the API handler's `@Idempotent` scope — `ticketing/issue.ts`
 * and `sales/record.ts` today. The void and the refund left the till in #285 and
 * #294, and their scopes (`ticketing.void`, `sales.refund.terminal`) stay here:
 * a till updated with either still in its queue drains it, and a refusal of one
 * still has to be described. Nothing
 * session-authenticated is ever queued: a back-office write goes over the wire
 * while somebody waits for it, and the queue is what the terminal has instead.
 *
 * Left as a plain record rather than a `satisfies Record<Scope, …>` over a union
 * of scopes, because there is no such union to import. The scopes live in the
 * desktop main process and the web bundle cannot reach it, which is the same
 * reason the lookup below tolerates a string it does not know.
 */
const DESCRIPTIONS: Readonly<Record<string, QueuedWriteDescription>> = {
  'ticketing.issue': {
    what: 'A gate ticket',
    // The ticket is printed before it is queued — that ordering is what keeps
    // the gate moving through an outage, and it is also what makes this the
    // gravest row on the screen. The visitor is inside the garden either way.
    stake:
      'It was printed at the gate and somebody was admitted on it. The server has no record of that admission, so the ticket is not in the day’s count and cannot be voided or checked anywhere.',
    actsOnAnExistingRecord: false,
  },

  'ticketing.void': {
    what: 'A cancelled gate ticket',
    stake:
      'The ticket was cancelled at the gate and the visitor was not admitted on it. The server still counts it as issued, and will keep doing so.',
    actsOnAnExistingRecord: true,
  },

  'sales.record': {
    what: 'A sale',
    // Goods or admission: since #274 a gate party on a taxed price is a sale,
    // and the scope cannot say which this was — the bridge carries no payload.
    stake:
      'The goods left the counter, or the visitors were admitted on the invoice, and the money is in the drawer. The server has no record of it, so the takings will not balance and nothing here can be refunded or reprinted.',
    actsOnAnExistingRecord: false,
  },

  'sales.refund.terminal': {
    what: 'A refund',
    // Deliberately `sales.refund.terminal` and not `sales.refund`: the back
    // office has its own refund route under the shorter scope, and it is not a
    // write that can ever be sitting in a terminal's queue.
    stake:
      'The money was paid back out of the drawer. The server still counts it as taken, so the takings will not balance until this lands.',
    actsOnAnExistingRecord: true,
  },
};

/**
 * What this write was, or `null` for a scope this build has not been taught.
 *
 * **Null rather than a general-purpose sentence.** A terminal's queue is a
 * SQLite file that outlives any one release, and the bundle reading it can be
 * older than the build that wrote the row — a scope added by a later ticket
 * belongs to a write this one genuinely knows nothing about. Prose invented for
 * it would be this screen telling somebody what they have lost while having no
 * idea, on the one screen where being confidently wrong costs money. The caller
 * falls back to the scope itself, which is at least a true fact that can be read
 * down the phone.
 */
export function describeQueuedWrite(scope: string): QueuedWriteDescription | null {
  // `Object.hasOwn` rather than a bare lookup, for the reason `messageForErrorCode`
  // gives about the same shape: `DESCRIPTIONS['constructor']` resolves up the
  // prototype chain to a function rather than missing, and the scope is a string
  // read out of a SQLite file on a device somebody has access to.
  //
  // The `?? null` is not dead code behind that guard — `noUncheckedIndexedAccess`
  // types the read as possibly `undefined` and TypeScript cannot see that the
  // check settled it. One null-ish value leaves this function either way.
  return Object.hasOwn(DESCRIPTIONS, scope) ? (DESCRIPTIONS[scope] ?? null) : null;
}
